import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, PencilSimple, Trash, UserCircle } from "@phosphor-icons/react";
import { Modal, Field } from "./Suppliers";
import { toast } from "sonner";

const DESIGNATIONS = ["Assembly", "Operator", "QC", "Packing", "Supervisor", "Other"];

export default function Staff() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const canDelete = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/staff");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this staff member? Their historical schedules and timesheets will remain in the database.")) return;
    try { await api.delete(`/staff/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Staff</h1>
          <p className="text-xs text-zinc-600 mt-1">Assembly folks, operators & shop-floor team. No login required.</p>
        </div>
        {canEdit && (
          <button onClick={openNew} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="new-staff-btn">
            <Plus size={14} weight="bold" /> Add Staff
          </button>
        )}
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="brut-table" data-testid="staff-table">
          <thead><tr><th>Name</th><th>Emp Code</th><th>Designation</th><th>Phone</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-zinc-500">No staff yet. Add your first team member.</td></tr>}
            {items.map((s) => (
              <tr key={s.id} data-testid={`staff-row-${s.name}`}>
                <td className="font-bold inline-flex items-center gap-2"><UserCircle size={16} weight="bold" /> {s.name}</td>
                <td className="font-mono-ui text-xs">{s.code || "—"}</td>
                <td><span className="brut-tag brut-tag-yellow">{s.designation}</span></td>
                <td className="font-mono-ui text-xs">{s.phone || "—"}</td>
                <td>{s.active ? <span className="brut-tag brut-tag-green">Active</span> : <span className="brut-tag brut-tag-dark">Inactive</span>}</td>
                <td className="text-right whitespace-nowrap">
                  {canEdit && (
                    <button onClick={() => openEdit(s)} className="brut-tag brut-tag-white mr-1" data-testid={`edit-staff-${s.name}`}>
                      <PencilSimple size={10} weight="bold" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => remove(s.id)} className="brut-tag brut-tag-red" data-testid={`delete-staff-${s.name}`}>
                      <Trash size={10} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <StaffModal
          initial={editing}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={() => { setOpen(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function StaffModal({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [code, setCode] = useState(initial?.code || "");
  const [designation, setDesignation] = useState(initial?.designation || "Operator");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { name: name.trim(), code: code.trim() || null, designation, phone, active };
      if (initial?.id) await api.put(`/staff/${initial.id}`, payload);
      else await api.post("/staff", payload);
      toast.success(initial ? "Updated" : "Added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={initial ? "Edit Staff" : "Add Staff"} onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <Field label="Name *" required value={name} onChange={setName} testid="staff-name" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Emp Code" value={code} onChange={setCode} testid="staff-code" placeholder="e.g. EMP-001" />
          <div>
            <label className="brut-label">Designation *</label>
            <select value={designation} onChange={(e) => setDesignation(e.target.value)} className="brut-input mt-1" data-testid="staff-designation">
              {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
        <Field label="Phone" value={phone} onChange={setPhone} testid="staff-phone" />
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="staff-active" />
          Active (appears in schedule)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="brut-btn px-3 py-2">Cancel</button>
          <button type="submit" disabled={saving} className="brut-btn brut-btn-primary px-3 py-2" data-testid="save-staff-btn">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
